import { action, makeObservable, observable } from "mobx";
import {
	fetchProtectedResource,
	fetchResource,
	isNumeric,
} from "../../utilities";
import { message } from "antd";
import { isArray } from "lodash";
import BaseService from "../base/BaseService";
import {
	CLAIM_MESSAGES_URL,
	CLAIM_URL,
	CLAIM_READ_URL,
	CUSTOMER_CLAIM_MESSAGE,
	QUOTE_CLAIM_URL,
	QUOTE_CLAIMS_URL,
	QUOTE_NOTES_URL,
} from "../../configs/api-paths";

/**
 * MobX service containing quote renewals.
 */
class ClaimsService extends BaseService {
	/**
	 * Claims about all quotes
	 * @type {[]}
	 */
	@observable
	claimsData = [];

	/**
	 * Is the service loading data.
	 * @type {boolean}
	 */
	@observable
	loading = false;

	/**
	 * Claims table pagination object.
	 * @type {{}}
	 */
	@observable
	pagination = {};

	/**
	 * Claims table sorting object.
	 * @type {{}}
	 */
	@observable
	sorter = {
		field: "renewalDate",
		order: "descend",
	};

	@observable
	search = {};

	constructor(props) {
		super(props);
		makeObservable(this);
	}

	@action
	setClaimsData = (data) => {
		this.claimsData = data;
	};

	@action
	setLoading = (loading) => {
		this.loading = loading;
	};

	@action
	setPagination = (pagination) => {
		this.pagination = pagination;
	};

	@action
	setSorter = (sorter) => {
		this.sorter = sorter;
	};

	@action
	setSearch = (search) => {
		this.search = search;
	};

	/**
	 * @returns {Promise<void>}
	 */
	loadClaimsData = async () => {
		this.setLoading(true);
		let hideLoadingMessage = message.loading("Loading...", 0);

		try {
			let field = this.sorter.field;
			const order = this.sorter.order;

			const currentPage = this.pagination.current;
			const pageSize = this.pagination.pageSize;

			let url = `${QUOTE_CLAIMS_URL}?`;

			if (field && order) {
				if (field === "insurer") {
					field = "product.insurer.name";
				}

				if (field === "product") {
					field = "product.name";
				}

				if (field === "first_name") {
					field = "organisation.first_name";
				}

				if (field === "last_name") {
					field = "organisation.last_name";
				}

				if (field === "phone") {
					field = "organisation.phone";
				}

				if (field === "address1") {
					field = "organisation.address1";
				}

				if (field === "address2") {
					field = "organisation.address2";
				}

				if (field === "city") {
					field = "organisation.city";
				}

				if (field === "post_code") {
					field = "organisation.post_code";
				}

				url += `sort=${field},${order === "ascend" ? "asc" : "desc"}&`;
			}

			if (isNumeric(currentPage) && isNumeric(pageSize)) {
				url += `page=${currentPage - 1}&size=${pageSize}&`;
			}

			if (isArray(this.search.fields)) {
				url += `searchFields=${this.search.fields.join(",")}&`;
				url += `searchValue=${this.search.value}&`;
			}

			const result = await fetchProtectedResource(url);
			hideLoadingMessage();
			const pagedResult = await result.json();
			// flattening array of arrays since array of claims is returned for each quote
			const pagedClaimsResults = [].concat.apply([], pagedResult.content);
			this.setPagination({
				...this.pagination,
				total: pagedClaimsResults.totalElements,
			});
			this.setClaimsData(pagedClaimsResults);
		} catch (error) {
			console.log(error);

			hideLoadingMessage();
			message.error(
				`There was error while trying to fetch claims data from the server. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	saveQuoteNotes = async (id, notes) => {
		this.setLoading(true);

		try {
			let url = `${QUOTE_NOTES_URL.replace("{id}", id)}`;

			await fetchProtectedResource(url, {
				method: "PUT",
				body: {
					notes,
				},
			});
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to save notes for this quote. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	sendClaimMessage = async (claimMessage) => {
		this.setLoading(true);

		try {
			await fetchResource(CUSTOMER_CLAIM_MESSAGE, {
				method: "POST",
				body: claimMessage,
			});
			message.success("Message has been sent", 5);
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to send claim message. Error: ${error}`
			);
		} finally {
			this.setLoading(false);
		}
	};

	saveClaimData = async (claim, admin) => {
		this.setLoading(true);
		const url = `${CLAIM_URL}?admin=${admin}`;
		try {
			const result = await fetchProtectedResource(url, {
				method: "PUT",
				body: claim,
			});
			if (result.ok) message.success(`Claim has been updated successfully!`, 5);
		} catch (error) {
			const errorResponse = await error.response.json();
			message.error(
				`There was error while trying to save claim data. Error: ${errorResponse.message}`,
				10
			);
		} finally {
			this.setLoading(false);
		}
	};

	updateIsMsgRead = async (claimId) => {
		const url = `${CLAIM_READ_URL}`;
		try {
			const result = await fetchProtectedResource(url, {
				method: "POST",
				body: {'claimId' :claimId},
			});
		} catch (error) {
			
		} 
	};

	fnol = async (claim) => {
		this.setLoading(true);
		try {
			const result = await fetchResource(CLAIM_URL, {
				method: "POST",
				body: claim,
			});
			if (result.ok) message.success(`Loss has been successfully notified!`, 5);
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to notify a loss. Error: ${error}`,
				10
			);
		} finally {
			this.setLoading(false);
		}
	};

	fetchClaimMessages = async (id) => {
		this.setLoading(true);
		try {
			const url = CLAIM_MESSAGES_URL.replace("{id}", id);
			const result = await fetchResource(url);
			return await result.json();
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to fetch messages. Error: ${error}`,
				10
			);
		} finally {
			this.setLoading(false);
		}
	};

	fetchQuoteData = async (quoteId) => {
		this.setLoading(true);
		try {
			const url = QUOTE_CLAIM_URL.replace("{quoteId}", quoteId);
			const result = await fetchProtectedResource(url);
			return await result.json();
		} catch (error) {
			console.log(error);

			message.error(
				`There was error while trying to fetch quote data. Error: ${error}`,
				10
			);
		} finally {
			this.setLoading(false);
		}
	};
}

export default ClaimsService;
